import { defineStore } from 'pinia';

export const usePathwaysStore = defineStore('pathways', {
  state: () => ({
    // Modal state
    newPathwayEnrolled: false,
    changePathway: false,

    // For enrollment flow
    pathwayId: null,
    globalPathwayUrlNavigationParam: '',

    // Main pathway data from /api/pathways/my-pathway endpoint
    // Contains everything: state, recommendedPathways (for new/recommendations),
    // or full pathway details (for enrolled)
    myPathway: null,

    // Loading state
    isLoading: false,

    // Legacy - kept for backward compatibility but deprecated
    pathwayDegreeCourses: {},
    recommendedPathway: null,
    recommendedPathways: [],
    pathwayTotalMandateCourse: 0,
    totalCourses: 0
  }),

  actions: {
    /**
     * Set my pathway data from /api/pathways/my-pathway endpoint
     * This is the primary data source - handles all 3 states:
     * - "new": No enrollments, default recommendations
     * - "recommendations": Has enrollments, not enrolled in pathway
     * - "enrolled": Enrolled in a pathway with full details
     */
    setMyPathwayData(payload) {
      this.myPathway = payload;
    },

    /**
     * Clear my pathway data (e.g., on unenroll or logout)
     */
    clearMyPathwayData() {
      this.myPathway = null;
    },

    setLoading(value) {
      this.isLoading = value;
    },

    // Modal actions
    setNewPathwayModal(payload) {
      this.newPathwayEnrolled = payload;
    },
    setChangePathwayModal(payload) {
      this.changePathway = payload;
    },

    // Enrollment flow actions
    setPathwayId(id) {
      this.pathwayId = id;
    },
    setGlobalPathwayUrlNavigationParam(param) {
      this.globalPathwayUrlNavigationParam = param;
    },

    // Legacy actions - kept for backward compatibility
    setPathwayData(payload) {
      this.pathwayDegreeCourses = payload;
    },
    setPathwayTotalMandateCourse(payload) {
      this.pathwayTotalMandateCourse = payload;
    },
    setTotalCourses(count) {
      this.totalCourses = count;
    },
    setRecommendedPathway(payload) {
      this.recommendedPathway = payload;
    },
    setRecommendedPathways(payload) {
      this.recommendedPathways = payload;
    }
  },

  getters: {
    /**
     * Get the current pathway state: "new", "recommendations", or "enrolled"
     */
    pathwayState: (state) => state.myPathway?.state || null,

    /**
     * Check if user is enrolled in a pathway
     */
    isEnrolled: (state) => state.myPathway?.state === 'enrolled',

    /**
     * Check if user has recommendations (state is "new" or "recommendations")
     */
    hasRecommendations: (state) => {
      const s = state.myPathway?.state;
      return s === 'new' || s === 'recommendations';
    },

    /**
     * Get recommended pathways array (for "new" and "recommendations" states)
     */
    getRecommendedPathways: (state) => state.myPathway?.recommendedPathways || [],

    /**
     * Get the enrolled pathway data (for "enrolled" state)
     * Returns the full myPathway object which contains all pathway details
     */
    getEnrolledPathway: (state) => {
      if (state.myPathway?.state === 'enrolled') {
        return state.myPathway;
      }
      return null;
    },

    /**
     * Get contact and partner info
     */
    getContactId: (state) => state.myPathway?.contactId || null,
    getPartnerId: (state) => state.myPathway?.partnerId || null,
    getCreditsNeeded: (state) => state.myPathway?.creditsNeeded || null,

    /**
     * Get my pathway data (full response)
     */
    getMyPathway: (state) => state.myPathway,

    // Legacy getters - kept for backward compatibility
    getPathwayData: (state) => state.pathwayDegreeCourses,
    getNewPathwayEnrolled: (state) => state.newPathwayEnrolled,
    getPathwayTotalMandateCourse: (state) => state.pathwayTotalMandateCourse,
    getChangePathway: (state) => state.changePathway,
    getRecommendedPathway: (state) => state.recommendedPathway,

    // Old getter name - maps to new logic
    isEnrolledInPathway: (state) => state.myPathway?.state === 'enrolled',
    getCreditCompletion: (state) => state.myPathway?.creditCompletion || null
  }
});
